import React, { useEffect, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import { CalculatingFit } from "./CalculatingFit";
import { SparkleGlyph } from "./SparkleGlyph";
import { CenterPanelContainer } from "./styles";
import { COLUMN_HEIGHT } from "../../constants";
import { FlipIcon } from "../icons/FlipIcon";
import type { MSHatData } from "../../types";
import { FacetInfoModal } from "../../../components/FacetInfoModal";
import type { FacetDefinition } from "../../../components/FacetInfoModal";
import {
  HAT_HIGH_INTEREST_THRESHOLD,
  HAT_LOW_INTEREST_THRESHOLD,
  HAT_MIN_VALID_SCORE,
} from "../../../constants/interestThresholds";

import { toSrc } from "../../utils/toSrc";

import seeYourFitIconImport from "./see-your-fit.svg";

const seeYourFitIcon = toSrc(seeYourFitIconImport as string | { src: string });

interface BackCenterPanelProps {
  hat: MSHatData;
  onFlip: () => void;
  cmsFacets?: FacetDefinition[];
  userHighInterestRiasec?: string[];
  userLowInterestRiasec?: string[];
  userSomeInterestRiasec?: string[];
  userTopWorkValues?: string[];
  onDiscoverCore?: () => void;
  onDiscoverWorkValues?: () => void;
  /** When true, render fit results without showing the "See Your Fit" button or calculating animation. */
  fitRevealed?: boolean;
  /** Fires once the calculating animation completes (i.e. the user just revealed). */
  onFitRevealed?: () => void;
  clusterColor?: string;
}

type InterestTier = "high" | "some" | "low";

// --- Data mappings ---

const RIASEC_SCORE_TO_FACET: Record<string, string> = {
  realistic: "RIASEC:R",
  investigative: "RIASEC:I",
  artistic: "RIASEC:A",
  social: "RIASEC:S",
  enterprising: "RIASEC:E",
  conventional: "RIASEC:C",
};

const WORK_VALUE_SCORE_TO_FACET: Record<string, string> = {
  achievement: "Accomplishing Goals",
  independence: "Being Independent",
  recognition: "Getting Recognition",
  relationships: "Building Relationships",
  support: "Trust & Support",
  workingConditions: "Stability & Security",
};

// Object key order is the canonical RIASEC order: Doer, Analyzer, Creator,
// Helper, Persuader, Organizer.
const RIASEC_KEYS = Object.keys(RIASEC_SCORE_TO_FACET);
const WORK_VALUE_KEYS = Object.keys(WORK_VALUE_SCORE_TO_FACET);

// --- Fit scoring ---

// Points earned when the hat's interest tier for a core type meets the user's
// tier for that same type. Outer key = hat tier, inner key = user tier.
// Exact-tier matches score positive (high 3, low 2, some 1); a one-step tier
// gap is neutral; the full high↔low gap is penalised. (Matrix is symmetric.)
const CORE_TIER_MATCH_POINTS: Record<
  InterestTier,
  Record<InterestTier, number>
> = {
  high: { high: 3, some: 0, low: -1 },
  some: { high: 0, some: 1, low: 0 },
  low: { high: -1, some: 0, low: 2 },
};

type FitCategoryKey = "mustTry" | "mightLike" | "mixed" | "mightNotLike";

// The four fit tags in descending order of fit score. All share the purple
// sparkle chip; only the label and the trailing sentence change. `tone` is the
// sentence after "You have N fits for <Hat>." — mightNotLike uses a bespoke
// sentence rendered inline, so its tone is null.
const FIT_CATEGORY: Record<
  FitCategoryKey,
  { label: string; tone: string | null }
> = {
  mustTry: {
    label: "Must Try",
    tone:
      "Strong signals point to a real fit here — explore the details of this " +
      "Hat to confirm what you're seeing.",
  },
  mightLike: {
    label: "Might Like",
    tone:
      "There are some great signs of a fit here — explore the details of this " +
      "Hat to see what catches your eye!",
  },
  mixed: {
    label: "Mixed Signals",
    tone:
      "You might find a few things you like and a few things you don't — " +
      "explore the details to see what you think.",
  },
  mightNotLike: { label: "Might Not Like", tone: null },
};

const getTier = (score: number): InterestTier => {
  if (score >= HAT_HIGH_INTEREST_THRESHOLD) return "high";
  if (score <= HAT_LOW_INTEREST_THRESHOLD) return "low";
  return "some";
};

const TIER_LABEL: Record<InterestTier, string> = {
  high: "High Interest",
  some: "Some Interest",
  low: "Low Interest",
};

const TIER_LABEL_COLOR: Record<InterestTier, string> = {
  high: "#7a4100",
  some: "#b76e00",
  low: "#919eab",
};

const getTileBackground = (tier: InterestTier, color?: string) => {
  if (tier === "low") return "#ffffff";
  const base = color || "#078DEE";
  const overlay =
    tier === "high" ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.9)";
  return `linear-gradient(${overlay}, ${overlay}), ${base}`;
};

// --- Styled components ---

const CardContainer = styled(Box, {
  label: "BackCenterCard",
  shouldForwardProp: (prop) => prop !== "borderColor",
})<{ borderColor?: string }>(({ borderColor }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: "#000000",
  border: "4px solid transparent",
  transition: "border-color 0.2s ease",
  ...(borderColor && {
    "&:has(.BackCenterFlipButton-root:hover)": {
      borderColor,
    },
  }),
}));

// The hat card image, flipped horizontally, fills the card as its backdrop.
const CardBackgroundImage = styled("img", {
  label: "BackCenterCardBackgroundImage",
})(() => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  transform: "scaleX(-1)",
  pointerEvents: "none",
}));

// Transparent black overlay sits over the flipped image.
const CardBackgroundOverlay = styled(Box, {
  label: "BackCenterCardBackgroundOverlay",
})(() => ({
  position: "absolute",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.75)",
  pointerEvents: "none",
}));

const CardContentPositioner = styled(Box, {
  label: "BackCenterCardContentPositioner",
})(() => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
}));

const CardContent = styled(Box, {
  label: "BackCenterCardContent",
})(() => ({
  width: 368,
  height: 569,
  display: "flex",
  flexDirection: "column",
  padding: "12px 24px 88px",
  gap: 16,
  alignItems: "center",
  transform: `scale(calc(${COLUMN_HEIGHT} / 569px))`,
  transformOrigin: "top center",
}));

const SectionHeading = styled(Typography, {
  label: "BackCenterSectionHeading",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textTransform: "uppercase",
  textAlign: "center",
  width: "100%",
}));

const SectionPrompt = styled(Typography, {
  label: "BackCenterSectionPrompt",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

// Groups a section heading with its list. In Figma the heading + list sit in a
// container with gap 8 ("Core Types" / "Work Values"); only the groups are
// separated by the outer 16px gap.
const SectionGroup = styled(Box, {
  label: "BackCenterSectionGroup",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
  width: "100%",
}));

const CoreList = styled(Box, {
  label: "BackCenterCoreList",
})(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  justifyContent: "center",
  width: "100%",
}));

const CoreTile = styled(Box, {
  label: "BackCenterCoreTile",
  shouldForwardProp: (prop) =>
    prop !== "tier" && prop !== "facetColor" && prop !== "matched",
})<{ tier: InterestTier; facetColor?: string; matched?: boolean }>(
  ({ tier, facetColor, matched }) => ({
    position: "relative",
    width: 148,
    height: 54,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 8,
    cursor: "pointer",
    background: getTileBackground(tier, facetColor),
    ...(matched && {
      outline: "2px solid #8c31fc",
      outlineOffset: "-2px",
    }),
  }),
);

const TileDarken = styled(Box, {
  label: "BackCenterTileDarken",
})(() => ({
  position: "absolute",
  inset: 0,
  borderRadius: 8,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  pointerEvents: "none",
}));

const FitBadge = styled(Box, {
  label: "BackCenterFitBadge",
})(() => ({
  position: "absolute",
  top: -9,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: 3,
  height: 17,
  padding: "2px 7px",
  borderRadius: 999,
  backgroundColor: "#8c31fc",
  zIndex: 2,
}));

const FitBadgeText = styled("span", {
  label: "BackCenterFitBadgeText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "9px",
  lineHeight: "12px",
  letterSpacing: "0.18px",
  color: "#ffffff",
}));

// Purple "Fit" pill anchored to the top edge of a matched tile.
const FitTag = () => (
  <FitBadge>
    <SparkleGlyph />
    <FitBadgeText>Fit</FitBadgeText>
  </FitBadge>
);

const TileType = styled(Box, {
  label: "BackCenterTileType",
})(() => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 4,
}));

const TileName = styled(Typography, {
  label: "BackCenterTileName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const TileInterest = styled(Typography, {
  label: "BackCenterTileInterest",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "10px",
  lineHeight: "12px",
  color: TIER_LABEL_COLOR[tier],
}));

const BarTrack = styled(Box, {
  label: "BackCenterBarTrack",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: 6,
  height: 42,
  flexShrink: 0,
  borderRadius: 50,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  backgroundColor:
    tier === "low" ? "rgba(145,158,171,0.16)" : "rgba(255,171,0,0.16)",
}));

const BarFill = styled(Box, {
  label: "BackCenterBarFill",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: "100%",
  borderRadius: 50,
  backgroundColor: tier === "low" ? "#c4cdd5" : "#ffab00",
}));

const WorkValuesRow = styled(Box, {
  label: "BackCenterWorkValuesRow",
})(() => ({
  display: "flex",
  gap: 24,
  justifyContent: "center",
  width: "100%",
}));

const WorkValueItem = styled(Box, {
  label: "BackCenterWorkValueItem",
  shouldForwardProp: (prop) => prop !== "matched",
})<{ matched?: boolean }>(({ matched }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 8,
  boxSizing: "border-box",
  padding: "2px 8px 2px 2px",
  borderRadius: 8,
  cursor: "pointer",
  border: `2px solid ${matched ? "#8c31fc" : "transparent"}`,
}));

const WorkValueIcon = styled(Box, {
  label: "BackCenterWorkValueIcon",
})(() => ({
  width: 40,
  height: 40,
  borderRadius: "100%",
  backgroundColor: "#c684ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& img": {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: "100%",
  },
}));

const WorkValueName = styled(Typography, {
  label: "BackCenterWorkValueName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#ffffff",
}));

const WorkValueDarken = styled(Box, {
  label: "BackCenterWorkValueDarken",
})(() => ({
  position: "absolute",
  inset: -2,
  borderRadius: 8,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  pointerEvents: "none",
}));

const SectionDivider = styled(Box, {
  label: "BackCenterDivider",
})(() => ({
  width: 320,
  height: 1,
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  flexShrink: 0,
}));

const SeeYourFitButton = styled("button", {
  label: "BackCenterSeeYourFit",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  height: 48,
  minWidth: 64,
  padding: "0 16px",
  borderRadius: 8,
  border: "1px solid rgba(145, 158, 171, 0.32)",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  color: "#1c252e",
  transition: "background-color 0.2s ease",
  "&:hover": { backgroundColor: "#f4f6f8" },
}));

// Fit summary: a purple chip ("Must Try" / "Might Not Like") stacked above a
// short message, shown after the fit reveal in place of the "See Your Fit"
// button.
const FitMessageGroup = styled(Box, {
  label: "BackCenterFitMessageGroup",
})(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  width: "100%",
}));

const FitChip = styled(Box, {
  label: "BackCenterFitChip",
})(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 9px",
  borderRadius: 999,
  backgroundColor: "#8c31fc",
}));

const FitChipText = styled("span", {
  label: "BackCenterFitChipText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "11px",
  lineHeight: "16px",
  color: "#ffffff",
}));

const FitMessage = styled(Typography, {
  label: "BackCenterFitMessage",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  color: "rgba(255, 255, 255, 0.6)",
  textAlign: "center",
  maxWidth: 272,
}));

// Bold, full-white emphasis (hat name / fit count) inside the fit message.
const FitEmphasis = styled("strong", {
  label: "BackCenterFitEmphasis",
})(() => ({
  fontWeight: 600,
  color: "#ffffff",
}));

const FlipButton = styled("button", {
  label: "BackCenterFlipButton",
})(() => ({
  border: "none",
  padding: 0,
  outline: "none",
  "&:focus-visible": {
    outline: "2px solid #fa680d",
    outlineOffset: 2,
  },
  position: "absolute",
  bottom: 19,
  left: "50%",
  transform: "translateX(-50%)",
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

FlipButton.defaultProps = { className: "BackCenterFlipButton-root" };

const FlipLabel = styled(Typography, {
  label: "BackCenterFlipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

export const BackCenterPanel = ({
  hat,
  onFlip,
  cmsFacets = [],
  userHighInterestRiasec,
  userLowInterestRiasec,
  userSomeInterestRiasec,
  userTopWorkValues,
  fitRevealed = false,
  onFitRevealed,
  clusterColor,
}: BackCenterPanelProps) => {
  const [showFit, setShowFit] = useState(fitRevealed);
  const [calculating, setCalculating] = useState(false);
  const [selectedFacet, setSelectedFacet] = useState<FacetDefinition | null>(
    null,
  );

  const openFacet = (facet?: FacetDefinition) => {
    if (facet) setSelectedFacet(facet);
  };

  useEffect(() => {
    if (fitRevealed) setShowFit(true);
  }, [fitRevealed]);

  const handleSeeYourFit = () => setCalculating(true);
  const handleCalculatingComplete = () => {
    setCalculating(false);
    setShowFit(true);
    onFitRevealed?.();
  };

  const facetMap = new Map(cmsFacets.map((f) => [f.facetCode, f]));

  const userHighSet = new Set(userHighInterestRiasec || []);
  const userLowSet = new Set(userLowInterestRiasec || []);
  const userSomeSet = new Set(userSomeInterestRiasec || []);
  const userWorkValueSet = new Set(userTopWorkValues || []);

  // All six core values in canonical RIASEC order, scored by the hat.
  const coreEntries = RIASEC_KEYS.map((key) => {
    const score = hat.riasecScores?.[key] || 0;
    const tier = getTier(score);
    const facet = facetMap.get(RIASEC_SCORE_TO_FACET[key]);
    const ratio = Math.max(0, Math.min(1, score / 7));
    const isMatch =
      score >= HAT_MIN_VALID_SCORE &&
      ((tier === "high" && userHighSet.has(key)) ||
        (tier === "low" && userLowSet.has(key)) ||
        (tier === "some" && userSomeSet.has(key)));
    return { key, score, tier, facet, ratio, isMatch };
  });

  // Top 2 work values for the hat.
  const workValueEntries = WORK_VALUE_KEYS.map((key) => ({
    key,
    score: hat.workValues?.[key] || 0,
    facet: facetMap.get(WORK_VALUE_SCORE_TO_FACET[key]),
  }))
    .filter((e) => e.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((e) => ({ ...e, isMatch: userWorkValueSet.has(e.key) }));

  const hasUserData =
    userHighSet.size > 0 ||
    userLowSet.size > 0 ||
    userSomeSet.size > 0 ||
    userWorkValueSet.size > 0;

  // Total matched facets (the purple "Fit" tags) — the "N fits" count shown in
  // the message. Distinct from the weighted fit score below.
  const fitCount =
    coreEntries.filter((e) => e.isMatch).length +
    workValueEntries.filter((e) => e.isMatch).length;
  const fitLabel = `${fitCount} fit${fitCount === 1 ? "" : "s"}`;
  const hatName = hat.displayName;

  // Weighted fit score → one of four fit tags. Each core type with a valid hat
  // score and a user tier contributes per CORE_TIER_MATCH_POINTS; each matched
  // top work value adds 1 point, capped at 2.
  const userTierOf = (key: string): InterestTier | null =>
    userHighSet.has(key)
      ? "high"
      : userSomeSet.has(key)
        ? "some"
        : userLowSet.has(key)
          ? "low"
          : null;

  const coreScoreBreakdown = coreEntries
    .filter((e) => e.score >= HAT_MIN_VALID_SCORE)
    .map((e) => {
      const userTier = userTierOf(e.key);
      return {
        key: e.key,
        hatTier: e.tier,
        userTier,
        points: userTier ? CORE_TIER_MATCH_POINTS[e.tier][userTier] : 0,
      };
    })
    .filter((b) => b.userTier !== null);

  const coreScore = coreScoreBreakdown.reduce((sum, b) => sum + b.points, 0);
  const workValueMatches = workValueEntries.filter((e) => e.isMatch).length;
  const workValuePoints = Math.min(workValueMatches, 2);
  const fitScore = coreScore + workValuePoints;
  const hasWorkValues = userWorkValueSet.size > 0;

  // Thresholds differ depending on whether the user has completed work values.
  const thresholds = hasWorkValues
    ? { mustTry: 7, mightLike: 4, mixed: 1 }
    : { mustTry: 6, mightLike: 3, mixed: 1 };
  const fitCategoryKey: FitCategoryKey =
    fitScore >= thresholds.mustTry
      ? "mustTry"
      : fitScore >= thresholds.mightLike
        ? "mightLike"
        : fitScore >= thresholds.mixed
          ? "mixed"
          : "mightNotLike";
  const fitCategory = FIT_CATEGORY[fitCategoryKey];

  // Debug: surface the fit-score breakdown while tuning the algorithm. Logged
  // as one pre-formatted string (header + only the point-scoring lines) so it
  // reads without expanding in the console.
  useEffect(() => {
    if (!showFit) return;
    const lines = coreScoreBreakdown
      .filter((b) => b.points !== 0)
      .map(
        (b) =>
          `  ${b.points > 0 ? "+" : ""}${b.points}  ${b.key} (hat ${b.hatTier} / user ${b.userTier})`,
      );
    if (workValuePoints !== 0) {
      lines.push(
        `  +${workValuePoints}  work values (${workValueMatches} matched)`,
      );
    }
    console.log(
      `[HatFit] ${hatName} → ${fitCategory.label} (score ${fitScore}${hasWorkValues ? "" : ", no work values"})` +
        (lines.length ? `\n${lines.join("\n")}` : ""),
    );
  }, [showFit, fitScore, hatName]);

  const backgroundUrl = hat.hatCard?.largeCard || hat.displayImage?.url;

  return (
    <CenterPanelContainer>
      <CardContainer borderColor={clusterColor}>
        {backgroundUrl && (
          <>
            <CardBackgroundImage src={backgroundUrl} alt="" />
            <CardBackgroundOverlay />
          </>
        )}
        <CardContentPositioner>
          <CardContent>
            {/* Core */}
            <SectionGroup>
              <Box sx={{ width: "100%" }}>
                <SectionHeading>Core</SectionHeading>
                <SectionPrompt>
                  What kind of person enjoys this work?
                </SectionPrompt>
              </Box>
              <CoreList>
                {coreEntries.map((entry) => {
                  const label = entry.facet?.variable || entry.key;
                  const darken = showFit && !entry.isMatch;
                  return (
                    <CoreTile
                      key={entry.key}
                      tier={entry.tier}
                      facetColor={entry.facet?.color}
                      matched={showFit && entry.isMatch}
                      onClick={() => openFacet(entry.facet)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openFacet(entry.facet);
                        }
                      }}
                    >
                      {showFit && entry.isMatch && <FitTag />}
                      {entry.facet?.icon ? (
                        <img
                          src={entry.facet.icon}
                          alt={label}
                          width={40}
                          height={40}
                          style={{ flexShrink: 0 }}
                        />
                      ) : (
                        <Box sx={{ width: 40, flexShrink: 0 }} />
                      )}
                      <TileType>
                        <TileName>{entry.facet?.longLabel || label}</TileName>
                        <TileInterest tier={entry.tier}>
                          {TIER_LABEL[entry.tier]}
                        </TileInterest>
                      </TileType>
                      <BarTrack tier={entry.tier}>
                        <BarFill
                          tier={entry.tier}
                          style={{
                            height: `${Math.round(entry.ratio * 100)}%`,
                          }}
                        />
                      </BarTrack>
                      {darken && <TileDarken />}
                    </CoreTile>
                  );
                })}
              </CoreList>
            </SectionGroup>

            <SectionDivider />

            {/* Work Values — tightened flanking gaps (negative margins pull the
                bracketing dividers in from the 16px column gap to 4px) and a
                smaller internal gap, so the fit message clears the flip button. */}
            <SectionGroup
              sx={{ gap: "4px", marginTop: "-12px", marginBottom: "-12px" }}
            >
              <Box sx={{ width: "100%" }}>
                <SectionHeading>Work Values</SectionHeading>
                <SectionPrompt>What makes this work rewarding?</SectionPrompt>
              </Box>
              {workValueEntries.length > 0 && (
                <WorkValuesRow>
                  {workValueEntries.map((wv) => {
                    const label =
                      wv.facet?.variable || wv.facet?.longLabel || wv.key;
                    const darken = showFit && !wv.isMatch;
                    return (
                      <WorkValueItem
                        key={wv.key}
                        matched={showFit && wv.isMatch}
                        onClick={() => openFacet(wv.facet)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openFacet(wv.facet);
                          }
                        }}
                      >
                        {showFit && wv.isMatch && <FitTag />}
                        <WorkValueIcon>
                          {wv.facet?.icon ? (
                            <img src={wv.facet.icon} alt={label} />
                          ) : (
                            <Typography sx={{ fontSize: 16, color: "#fff" }}>
                              {label.charAt(0).toUpperCase()}
                            </Typography>
                          )}
                        </WorkValueIcon>
                        <WorkValueName>{label}</WorkValueName>
                        {darken && <WorkValueDarken />}
                      </WorkValueItem>
                    );
                  })}
                </WorkValuesRow>
              )}
            </SectionGroup>

            {/* See Your Fit */}
            {calculating ? (
              <CalculatingFit onComplete={handleCalculatingComplete} />
            ) : !showFit && hasUserData ? (
              <>
                <SectionDivider />
                <SeeYourFitButton onClick={handleSeeYourFit}>
                  <img
                    src={seeYourFitIcon}
                    alt=""
                    width={24}
                    height={24}
                    style={{ flexShrink: 0 }}
                  />
                  See Your Fit
                </SeeYourFitButton>
              </>
            ) : showFit ? (
              <>
                <SectionDivider />
                <FitMessageGroup>
                  <FitChip>
                    <SparkleGlyph size={10} />
                    <FitChipText>{fitCategory.label}</FitChipText>
                  </FitChip>
                  <FitMessage>
                    {fitCategoryKey === "mightNotLike" ? (
                      <>
                        You have {fitLabel}.{" "}
                        <FitEmphasis>{hatName}</FitEmphasis> may not be a fit
                        for you – but explore the details of this Hat to know
                        for sure.
                      </>
                    ) : (
                      <>
                        You have <FitEmphasis>{fitLabel}</FitEmphasis> for{" "}
                        <FitEmphasis>{hatName}.</FitEmphasis> {fitCategory.tone}
                      </>
                    )}
                  </FitMessage>
                </FitMessageGroup>
              </>
            ) : null}

            <FlipButton onClick={onFlip} aria-label="Flip card to front">
              <FlipIcon />
              <FlipLabel>Flip</FlipLabel>
            </FlipButton>
          </CardContent>
        </CardContentPositioner>
      </CardContainer>
      <FacetInfoModal
        open={!!selectedFacet}
        onClose={() => setSelectedFacet(null)}
        facet={selectedFacet}
      />
    </CenterPanelContainer>
  );
};
